import type React from 'react';
import { Card } from '../tailgrids/core/card.js';
import { type ChartTone, TONE_SWATCH } from './chart-colors.js';
import { cn } from '../../utils/cn.js';

export interface LegendItem {
  label: string;
  tone: ChartTone;
}

export interface ChartStat {
  id: string;
  label: string;
  /** The headline figure ("$24,300"). */
  value: string;
  tone: ChartTone;
  /** Change since the last period ("+4.2%"). */
  delta?: string;
  /** Colours the delta: green when true, red when false. */
  positive?: boolean;
  /** After the delta ("last month"). */
  caption?: string;
}

interface ChartCardProps {
  title: string;
  description?: React.ReactNode;
  /** Controls on the right of the title (period select, export). */
  actions?: React.ReactNode;
  legend?: readonly LegendItem[];
  /** Headline figures above the chart, each with its series colour and change (sales-chart header). */
  stats?: readonly ChartStat[];
  children: React.ReactNode;
  className?: string;
}

/**
 * A chart in a card: title row, optional legend and headline stats, then the chart. Template example: NextAdmin
 * `charts/{bar,line,pie}-charts/_components/*` (`Card className="p-0"`, header `border-b border-card-border px-6 py-4`
 * with an `h3 text-base font-medium`, body `flex flex-col gap-5 p-6`) and the stats header of
 * `(dashboard)/(home)/_component/sales-chart/header.tsx`.
 */
export function ChartCard({ title, description, actions, legend, stats, children, className }: ChartCardProps) {
  return (
    <Card className={cn('p-0', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-card-border px-6 py-4">
        <div className="min-w-0">
          <h3 className="text-base font-medium text-text-primary">{title}</h3>
          {description && <p className="mt-0.5 text-sm text-text-tertiary">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      <div className="flex flex-col gap-5 p-6">
        {stats && stats.length > 0 && <ChartStats items={stats} />}
        {legend && legend.length > 0 && <ChartLegendList items={legend} />}
        {children}
      </div>
    </Card>
  );
}

/** A row of colour swatches naming the series. */
export function ChartLegendList({ items }: { items: readonly LegendItem[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span className={cn('size-2 shrink-0 rounded-xs', TONE_SWATCH[item.tone])} aria-hidden="true" />
          <span className="text-sm font-medium text-text-secondary">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Headline figures: a colour dot and label over the value, with the change beside it. */
function ChartStats({ items }: { items: readonly ChartStat[] }) {
  return (
    <div className="flex flex-col gap-8 sm:flex-row">
      {items.map((stat) => (
        <div key={stat.id}>
          <div className="mb-2 flex items-center gap-3">
            <span className={cn('size-2 rounded-xs', TONE_SWATCH[stat.tone])} aria-hidden="true" />
            <span className="text-sm leading-5 font-medium text-text-secondary">{stat.label}</span>
          </div>
          <div className="flex items-center gap-3">
            <h4 className="text-xl font-semibold text-text-primary md:text-2xl md:leading-8">{stat.value}</h4>
            {(stat.delta || stat.caption) && (
              <p className="flex items-center gap-1 text-sm leading-5 text-text-secondary">
                {stat.delta && (
                  <span className={cn('font-medium', stat.positive ? 'text-success-500' : 'text-error-500')}>
                    {stat.delta}
                  </span>
                )}
                {stat.caption && <span>{stat.caption}</span>}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
